import { useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  LayoutDashboard,
  Pencil,
  Plus,
  Search,
  Share2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useCan } from "@/lib/auth/useCan";

import { useArchiveReport, useReports, type ReportSummary } from "../api";
import { ReportFormDialog } from "../components/ReportFormDialog";
import { ShareDialog } from "../components/ShareDialog";

/**
 * Reports home: the list of reports the viewer can see (all of them with
 * reports.read; otherwise the ones they own or that were shared with them),
 * with create / edit / share / archive. The conversational builder + viewer
 * arrive in later phases.
 */
export function ReportsListPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const reports = useReports(includeArchived);
  const canWrite = useCan("reports.write");
  const archive = useArchiveReport();
  const navigate = useNavigate();

  const openBuilder = (r: ReportSummary) =>
    navigate({ to: "/reports/$reportId", params: { reportId: r.id } });
  const openViewer = (r: ReportSummary) =>
    navigate({ to: "/reports/$reportId/view", params: { reportId: r.id } });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ReportSummary | null>(null);
  const [sharing, setSharing] = useState<ReportSummary | null>(null);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (r: ReportSummary) => {
    setEditing(r);
    setFormOpen(true);
  };
  const toggleArchive = async (r: ReportSummary) => {
    const archiving = r.status !== "archived";
    try {
      await archive.mutateAsync({ id: r.id, archived: archiving });
      toast.success(archiving ? "Report archived" : "Report restored");
    } catch (e) {
      toast.error((e as Error)?.message ?? "Action failed");
    }
  };

  const list = useMemo(() => reports.data ?? [], [reports.data]);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return list;
    return list.filter((r) =>
      `${r.title} ${r.description ?? ""} ${r.created_by_name ?? ""}`.toLowerCase().includes(term),
    );
  }, [list, query]);

  return (
    <div className="mx-auto flex max-w-[90rem] flex-col gap-4 pb-10">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <LayoutDashboard className="h-6 w-6 text-brand" />
            Reports
          </h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Custom reports &amp; dashboards. Create one, then build it by describing the data
            you want to an AI agent (coming in the next phase).
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="show-archived"
              checked={includeArchived}
              onCheckedChange={setIncludeArchived}
            />
            <Label htmlFor="show-archived" className="text-xs text-muted-foreground">
              Show archived
            </Label>
          </div>
          {canWrite && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New report
            </Button>
          )}
        </div>
      </header>

      {reports.isError ? (
        <Alert variant="danger">
          <AlertTitle>Couldn’t load reports</AlertTitle>
          <AlertDescription>
            {(reports.error as Error)?.message ?? "Unknown error."}
          </AlertDescription>
        </Alert>
      ) : reports.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <LayoutDashboard className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">No reports yet</p>
          <p className="max-w-md text-sm text-muted-foreground">
            {canWrite
              ? "Create your first report to get started."
              : "You don’t have any reports yet. Ask a teammate to share one with you, or request the reports.write permission to create your own."}
          </p>
          {canWrite && (
            <Button className="mt-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              New report
            </Button>
          )}
        </Card>
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reports…"
              className="pl-8"
              aria-label="Search reports"
            />
          </div>
          {filtered.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 p-10 text-center">
              <Search className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">No reports match “{query}”</p>
              <p className="max-w-md text-sm text-muted-foreground">
                Try a different search, or clear it to see all reports.
              </p>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((r) => (
                <Card key={r.id} className="flex flex-col gap-3 p-4">
              <div className="flex items-start justify-between gap-2">
                <h2 className="min-w-0 flex-1 truncate text-sm font-bold" title={r.title}>
                  {r.title}
                </h2>
                {r.status === "archived" && <Badge variant="neutral">Archived</Badge>}
              </div>
              <p className="line-clamp-2 min-h-[2.5rem] text-xs text-muted-foreground">
                {r.description || "No description."}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                <Badge variant="secondary">
                  {r.panel_count} panel{r.panel_count === 1 ? "" : "s"}
                </Badge>
                {r.shared && <Badge variant="info">Shared with you</Badge>}
                {r.created_by_name && <span>· {r.created_by_name}</span>}
              </div>
              <div className="mt-auto flex flex-wrap gap-2 border-t border-border pt-3">
                <Button size="sm" onClick={() => openViewer(r)}>
                  Open
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
                {r.can_edit && (
                  <Button variant="outline" size="sm" onClick={() => openBuilder(r)}>
                    <Sparkles className="h-3.5 w-3.5" />
                    Build
                  </Button>
                )}
                {r.can_edit && (
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
                    <Pencil className="h-3.5 w-3.5" />
                    Rename
                  </Button>
                )}
                {r.can_manage && (
                  <Button variant="outline" size="sm" onClick={() => setSharing(r)}>
                    <Share2 className="h-3.5 w-3.5" />
                    Share
                  </Button>
                )}
                {r.can_manage && (
                  <Button variant="ghost" size="sm" onClick={() => toggleArchive(r)}>
                    {r.status === "archived" ? (
                      <>
                        <ArchiveRestore className="h-3.5 w-3.5" />
                        Restore
                      </>
                    ) : (
                      <>
                        <Archive className="h-3.5 w-3.5" />
                        Archive
                      </>
                    )}
                  </Button>
                )}
              </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <ReportFormDialog open={formOpen} onOpenChange={setFormOpen} report={editing} />
      <ShareDialog
        open={!!sharing}
        onOpenChange={(o) => {
          if (!o) setSharing(null);
        }}
        report={sharing}
      />
    </div>
  );
}
